import { matchControl } from "./control";
import type { Capture, Pending } from "./mic";
import { NotHeard } from "./recorder";

/**
 * A hands-free intake (CHANGE_voice_level3 §3): the agent speaks, the user answers, the agent acknowledges and the
 * next question follows, with no look or touch after the start tap. This is the whole state machine; everything it
 * does to the world goes through `HandsFreePorts`, so it knows nothing about the API, React or the audio hardware.
 *
 *   idle → speaking → listening → thinking → speaking …   plus paused, ended, error
 */
export type HandsFreeState = "idle" | "speaking" | "listening" | "thinking" | "typing" | "paused" | "ended" | "error";
export type Phrase = "ack" | "one_moment" | "say_again" | "still_there" | "paused" | "resumed" | "audio_changed" | "lost" | "stopped" | "need_that" | "nothing_before";

/** What the agent is saying now. A new `key` is a new turn; the same key again is ignored. */
export interface Turn {
  key: string;
  kind: "question" | "confirm" | "review" | "end";
  /** What is read aloud: the spoken prompt, the read-back line, the review summary, the closing line. */
  text: string;
  /** The question on its own, for "I need that one — {prompt}". */
  prompt?: string;
  required?: boolean;
  /** There is nothing before the first question on the New request page. */
  canGoBack?: boolean;
  /** Review rows, for "read all {label}". */
  review?: Array<{ label: string; full: string }>;
  /** The question was spoken sentence by sentence as it streamed in; only `remainder` (the options) is left to say,
   * and what is already queued must not be cut off. */
  streamed?: boolean;
  remainder?: string;
}

export type HandsFreeReply = "" | { text: string; via: "voice" } | { go_back: true; via: "voice" } | { confirm: true; via: "voice" };

export interface HandsFreePorts {
  /** Speak, resolving when finished or silenced. */
  say(text: string): Promise<void>;
  /** A fixed line from the cached clips. */
  clip(name: Phrase): Promise<void>;
  phrase(name: Phrase): string;
  /** Silence the agent now. */
  hush(): void;
  capture(opts: { timeoutMs: number; maxMs?: number }): Pending<Capture>;
  /** Barge-in: someone started talking over the agent. */
  watch(): Pending<boolean>;
  /** Throws NotHeard when the words came back but were not an answer. */
  transcribe(audio: Blob, ms: number): Promise<string>;
  send(reply: HandsFreeReply): Promise<void>;
}

export interface HandsFreeOptions {
  bargeIn: boolean;
  onState(state: HandsFreeState): void;
  /** What was heard and what was said, for the large-type transcript. */
  onLine?(role: "agent" | "user", text: string): void;
  /** No speech for this long: "Are you still there?"; as long again: paused. */
  noSpeechMs?: number;
  /** An answer sent and nothing back for this long: "One moment." (once). */
  nudgeMs?: number;
}

/** While paused, only something this short is transcribed: "resume", not the radio or a conversation. */
const COMMAND_MS = 4_000;

export class HandsFree {
  state: HandsFreeState = "idle";
  private turn: Turn | null = null;
  private token = 0;                       // bumped whenever what is under way is superseded
  private io: Array<{ cancel(): void }> = [];
  private nudge: ReturnType<typeof setTimeout> | null = null;
  private failures = 0;
  private readonly noSpeechMs: number;
  private readonly nudgeMs: number;
  private bargeIn: boolean;

  constructor(private readonly ports: HandsFreePorts, private readonly opts: HandsFreeOptions) {
    this.noSpeechMs = opts.noSpeechMs ?? 20_000;
    this.nudgeMs = opts.nudgeMs ?? 15_000;
    this.bargeIn = opts.bargeIn;
  }

  /** The per-device switch, from the next time the agent speaks. */
  setBargeIn(on: boolean): void { this.bargeIn = on; }

  get current(): Turn | null { return this.turn; }

  /** The page reports what the agent is saying now. Spoken at once unless paused (then on resume) or over. */
  setTurn(turn: Turn | null): void {
    if (!turn || turn.key === this.turn?.key) return;
    this.turn = turn;
    if (this.state === "paused" || this.state === "ended" || this.state === "error") return;
    void this.run(turn);
  }

  /** Stop listening and speaking until "resume" (or `resume()` from a tap). */
  pause(reason: "user" | "silence" | "interrupted" | "audio" = "user"): void {
    if (this.state === "paused" || this.state === "ended" || this.state === "error" || this.state === "idle") return;
    const my = this.supersede();
    this.set("paused");
    void (async () => {
      await this.ports.clip(reason === "audio" ? "audio_changed" : "paused");
      await this.listenWhilePaused(my);
    })();
  }

  /** Back from a pause: the current turn is said again, so nobody has to remember where they were. */
  resume(): void {
    if (this.state !== "paused") return;
    const my = this.supersede();
    this.set("speaking");
    void (async () => {
      await this.ports.clip("resumed");
      if (my === this.token && this.turn) await this.run(this.turn);
    })();
  }

  /** "Stop": the session ends, the run stays exactly where it is. */
  stop(): void {
    if (this.state === "ended" || this.state === "error") return;
    void this.end("stopped");
  }

  /** The user left the page: stop at once, say nothing. The run stays where it is. */
  quit(): void {
    if (this.state === "ended" || this.state === "error") return;
    this.finish();
  }

  /** The microphone or the network went: one line, then the screen takes over. The run is untouched. */
  fail(): void {
    if (this.state === "ended" || this.state === "error") return;
    this.supersede();
    this.set("error");
    void this.ports.clip("lost").catch(() => undefined);
  }

  /** Remind a paused session that it is paused (coming back to the page). */
  remind(): void {
    if (this.state === "paused") void this.ports.clip("paused");
  }

  // ---- the loop -----------------------------------------------------------------------------------

  /**
   * The person is typing: this turn is theirs. Listening stops and nothing is sent by voice until the next question
   * (they send what they typed themselves); the agent stops talking.
   */
  yieldTurn(): void {
    if (this.state !== "listening" && this.state !== "speaking" && this.state !== "thinking") return;
    this.supersede();
    this.set("typing");
  }

  private async run(turn: Turn): Promise<void> {
    const my = this.supersede(!turn.streamed);   // a streamed question's sentences are already queued: keep them
    this.set("speaking");
    this.opts.onLine?.("agent", turn.text);
    if (turn.kind === "end") {
      await this.ports.say(turn.text);
      if (my === this.token) this.finish();
      return;
    }
    if (this.bargeIn) {
      const watcher = this.track(this.ports.watch());
      void watcher.done.then((hit) => { if (hit && my === this.token && this.state === "speaking") this.ports.hush(); });
    }
    await this.ports.say(turn.streamed ? turn.remainder ?? "" : turn.text);
    if (my !== this.token) return;
    this.cancelIo();
    await this.listen(turn, my);
  }

  private async listen(turn: Turn, my: number): Promise<void> {
    let quiet = 0;
    for (;;) {
      if (my !== this.token) return;
      this.set("listening");
      const got = await this.track(this.ports.capture({ timeoutMs: this.noSpeechMs })).done;
      if (my !== this.token || got.kind === "cancelled") return;
      if (got.kind === "silence") {
        if (++quiet >= 2) return this.pause("silence");
        await this.speakPhrase("still_there", my);
        continue;
      }
      quiet = 0;
      this.set("thinking");
      let text: string;
      try {
        text = (await this.ports.transcribe(got.audio, got.ms)).trim();
        this.failures = 0;
      } catch (e) {
        if (my !== this.token) return;
        if (!(e instanceof NotHeard) && ++this.failures >= 2) return this.fail();
        await this.speakPhrase("say_again", my);
        continue;
      }
      if (my !== this.token) return;
      if (!text) { await this.speakPhrase("say_again", my); continue; }
      this.opts.onLine?.("user", text);
      if (await this.handle(turn, text, my) === "listen") continue;
      return;
    }
  }

  /** A control word acts here; anything else is the answer. "listen" means stay on this turn. */
  private async handle(turn: Turn, text: string, my: number): Promise<"listen" | "done"> {
    const control = matchControl(text, { review: turn.kind === "review", labels: turn.review?.map((r) => r.label) });
    switch (control?.kind) {
      case "repeat":
        await this.speak(turn.text, my);
        return "listen";
      case "skip":
        // an optional question is skipped; a required one is recorded as not known yet (to be confirmed) by the
        // server, which reads "skip", "pass" and "I'll have it later" alike
        if (turn.kind === "question") { await this.submit(turn.required ? { text, via: "voice" } : "", my); return "done"; }
        await this.speak(`${this.ports.phrase("need_that")} ${turn.prompt ?? turn.text}`, my);
        return "listen";
      case "go_back":
        if (turn.kind === "review" || turn.canGoBack === false) { await this.speakPhrase(turn.kind === "review" ? "say_again" : "nothing_before", my); return "listen"; }
        await this.submit({ go_back: true, via: "voice" }, my);
        return "done";
      case "pause":
        this.pause("user");
        return "done";
      case "stop":
        await this.end("stopped");
        return "done";
      case "confirm":
        await this.submit({ confirm: true, via: "voice" }, my);
        return "done";
      case "read_all": {
        const row = turn.review?.find((r) => r.label === control.label);
        if (row) await this.speak(`${row.label}: ${row.full}`, my);
        return "listen";
      }
      case "resume":   // not paused: nothing to resume, and not an answer either
        return "listen";
      default:
        await this.submit({ text, via: "voice" }, my);
        return "done";
    }
  }

  /** Send, say "Got it" from the cached clip straight away, and wait for the next turn to arrive. */
  private async submit(reply: HandsFreeReply, my: number): Promise<void> {
    this.set("thinking");
    void this.ports.clip("ack");
    this.nudge = setTimeout(() => { if (my === this.token && this.state === "thinking") void this.ports.clip("one_moment"); }, this.nudgeMs);
    try {
      await this.ports.send(reply);
    } catch {
      if (my === this.token) this.fail();
    }
  }

  private async listenWhilePaused(my: number): Promise<void> {
    for (;;) {
      if (my !== this.token) return;
      const got = await this.track(this.ports.capture({ timeoutMs: 60_000, maxMs: COMMAND_MS })).done;
      if (my !== this.token || got.kind === "cancelled") return;
      if (got.kind === "silence" || got.ms > COMMAND_MS) continue;
      let text = "";
      try { text = await this.ports.transcribe(got.audio, got.ms); } catch { continue; }
      if (my !== this.token) return;
      const control = matchControl(text, { paused: true });
      if (control?.kind === "resume") return this.resume();
      if (control?.kind === "stop") return void this.end("stopped");
    }
  }

  private async end(phrase: Phrase): Promise<void> {
    const my = this.supersede();
    this.set("speaking");
    await this.ports.clip(phrase);
    if (my === this.token) this.finish();
  }

  private finish(): void {
    this.supersede();
    this.set("ended");
  }

  // ---- plumbing -----------------------------------------------------------------------------------

  private async speak(text: string, my: number): Promise<void> {
    if (my !== this.token) return;
    this.set("speaking");
    this.opts.onLine?.("agent", text);
    await this.ports.say(text);
  }

  private async speakPhrase(name: Phrase, my: number): Promise<void> {
    if (my !== this.token) return;
    this.set("speaking");
    await this.ports.clip(name);
  }

  private supersede(hush = true): number {
    this.cancelIo();
    if (hush) this.ports.hush();
    return ++this.token;
  }

  private track<T extends { cancel(): void }>(p: T): T {
    this.io.push(p);
    return p;
  }

  private cancelIo(): void {
    if (this.nudge) clearTimeout(this.nudge);
    this.nudge = null;
    const io = this.io;
    this.io = [];
    io.forEach((p) => p.cancel());
  }

  private set(state: HandsFreeState): void {
    if (this.state === state) return;
    this.state = state;
    this.opts.onState(state);
  }
}
